<template>
  <div :class="twMerge('inline-block w-full', props.rootClass)">
    <TsLabel
      :label="props.label"
      :for="'textarea-' + randomId"
      :variant="props.variant"
      :input-value="textModelValue"
      :floating="props.floating"
      :label-class="twMerge('top-[20%]', floatingLabelClass)"
    >
      <textarea
        :id="'textarea-' + randomId"
        v-bind="$attrs"
        v-model="textModelValue"
        ref="textareaRef"
        @input="onTextareaInput($event)"
        @focus="emit('focus', $event, textareaRef)"
        @blur="emit('blur', $event, textareaRef)"
        @keydown.enter.exact="emit('enter', $event, textareaRef)"
        :class="$attrs?.class || twMerge(textareaClass, props.appendClass)"
        :rows="Number($attrs?.rows || 3)"
        autocomplete="off"
        spellcheck="false"
        :data-testid="props.dataTestid"
      />
      <template #message>
        <slot name="helper-msg" :textareaRef="textareaRef">
          <p
            v-show="props.variant"
            :class="
              twMerge(
                'flex mt-1 items-center',
                props.variant === 'danger' &&
                  'text-danger dark:text-danger-dark',
                props.variant === 'success' &&
                  'text-success dark:text-success-dark'
              )
            "
          >
            <TsIcon
              v-if="props.helperMessage"
              :name="props.helperMessageIcon"
              size="20"
              class="me-0.5 shrink-0"
              @click="emit('helperIconClick', textareaRef)"
            />
            <span class="text-xs-mobile sm:text-xs">
              {{ props.helperMessage }}
            </span>
          </p>
        </slot>
      </template>
    </TsLabel>
  </div>
</template>

<script setup lang="ts">
import { twMerge, type ClassNameValue } from "tailwind-merge";
import { ref, computed, onMounted, onUpdated } from "vue";
import { useRandomUUID } from "../../composables/useRandomUUID";

// attribute binding
defineOptions({
  inheritAttrs: false,
});

// types & props
const SIZES = ["xs", "sm", "md", "lg"] as const;
type SizeType = (typeof SIZES)[number];
const VARIANTS = ["success", "danger"] as const;
type VariantType = (typeof VARIANTS)[number];

type Props = {
  dataTestid?: string;
  label?: string;
  variant?: VariantType;
  size?: SizeType;
  resizeable?: boolean;
  autoresize?: boolean;
  floating?: boolean;
  helperMessage?: string;
  helperMessageIcon?: string;
  rootClass?: ClassNameValue;
  appendClass?: ClassNameValue;
};

const props = withDefaults(defineProps<Props>(), {
  dataTestid: "atom-textarea",
  label: "",
  size: "md",
  resizeable: false,
  autoresize: false,
  floating: false,
  helperMessage: "Form field error text",
  helperMessageIcon: "clarity:error-line",
  rootClass: "",
  appendClass: "",
});

// v-model
const textModelValue = defineModel<string>();

// template refs
const textareaRef = ref<HTMLTextAreaElement | null>(null);

// emits
const emit = defineEmits<{
  input: [e: Event, textareaRef: HTMLTextAreaElement | null];
  focus: [e: Event, textareaRef: HTMLTextAreaElement | null];
  blur: [e: Event, textareaRef: HTMLTextAreaElement | null];
  enter: [e: Event, textareaRef: HTMLTextAreaElement | null];
  helperIconClick: [textareaRef: HTMLTextAreaElement | null];
}>();

// Methods
function handleResize() {
  if (textareaRef.value) {
    // Set textarea height to auto at start to prevent it from taking scrollHeight directly
    textareaRef.value.style.height = "auto";
    if (textareaRef.value.scrollHeight)
      textareaRef.value.style.height = textareaRef.value.scrollHeight + "px";

    // Check if the new height exceeds the maximum allowed height
    if (
      parseFloat(textareaRef.value.style.height) >=
      parseFloat(textareaRef.value.style.maxHeight)
    ) {
      textareaRef.value.style.overflowY = "scroll";
      textareaRef.value.style.height = textareaRef.value.style.maxHeight;
    } else {
      textareaRef.value.style.overflow = "hidden";
    }
  }
}

const onTextareaInput = (e: Event) => {
  emit("input", e, textareaRef.value);
  if (!props.autoresize) return;
  handleResize();
};

// base classes
const baseClasses =
  "w-full max-w-full px-3 py-0 m-0 resize bg-transparent disabled:bg-natural-soft-white dark:disabled:bg-natural-dark-grey disabled:cursor-not-allowed text-idle-black rounded-md border border-natural-light-grey dark:border-natural-grey disabled:border-natural-light-grey focus:ring-0 focus:border-info dark:text-idle-white dark:focus:border-info-dark focus:outline-none disabled:text-natural-silver-grey dark:disabled:text-natural-grey";

const variantBasedClass: Record<VariantType, ClassNameValue> = {
  danger:
    "border-danger focus:border-danger-hover dark:border-danger-dark dark:focus:border-danger-dark-hover",
  success:
    "border-success focus:border-success-hover dark:border-success-dark dark:focus:border-success-dark-hover",
};
const sizeBasedClass: Record<SizeType, ClassNameValue> = {
  xs: "text-[12px] py-2 rounded",
  sm: "text-[14px] py-2.5 rounded-sm",
  md: "text-[16px] py-3",
  lg: "text-[18px] py-3.5 rounded-lg",
};

const sizeBasedLabelClass: Record<SizeType, string> = {
  xs: "text-[12px]",
  sm: "text-[14px]",
  md: "text-[16px]",
  lg: "text-[18px]",
};

const textareaClass = computed<ClassNameValue>(() =>
  twMerge(
    baseClasses,
    !props.resizeable && "resize-none",
    props.variant && variantBasedClass[props.variant],
    sizeBasedClass[props.size],
    props.autoresize && "hide-scroll-bar"
  )
);

const floatingLabelClass = computed<string>(
  () => sizeBasedLabelClass[props.size]
);

// random ID for each vue instance
const randomId: Ref<string> = ref("");

onMounted(() => {
  randomId.value = useRandomUUID();
  if (!props.autoresize) return;
  handleResize();
});

onUpdated(() => {
  if (!props.autoresize) return;
  handleResize();
});
</script>
